import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Alert, App, DatePicker, Input, InputNumber, Modal, Select, Spin } from 'antd'
import dayjs from 'dayjs'
import type { ServiceOrder, ServiceTermInput } from '@super-mega/server/contracts/order'
import { useTRPC } from '@/trpc/react.ts'
import { formatAmount } from '@/utils/money.ts'
import { ServiceTermEditor } from './service-term-fields.tsx'

export function ServiceTermActionDialog({
  action,
  order,
  rules,
  onClose,
  onSaved
}: {
  action: 'terminate' | 'reprice' | 'renew'
  order: ServiceOrder
  rules: { ruleId: number; feeName: string; chargeMode: string; periods: { key: string; periodStart: string }[] }[]
  onClose: () => void
  onSaved: () => Promise<void>
}) {
  const trpc = useTRPC()
  const { message } = App.useApp()
  const [open, setOpen] = useState(true)
  const [date, setDate] = useState(() => dayjs().format('YYYY-MM-DD'))
  const [reason, setReason] = useState('')
  const [ruleId, setRuleId] = useState<number>()
  const [price, setPrice] = useState<number | null>(null)
  const [term, setTerm] = useState<ServiceTermInput>(() => {
    const start = dayjs.utc(order.terminatedAt ?? order.endAt!).add(1, 'day')
    return {
      startAt: start.format('YYYY-MM-DD'),
      endAt: start.add(1, 'year').subtract(1, 'day').format('YYYY-MM-DD'),
      rules: order.term!.rules.filter((rule) => rule.feeType !== 'deposit')
    }
  })
  const [idempotencyKey] = useState(() => crypto.randomUUID())
  const terminate = useMutation(trpc.order.service.terminate.mutationOptions())
  const reprice = useMutation(trpc.order.service.reprice.mutationOptions())
  const renew = useMutation(trpc.order.service.renew.mutationOptions())
  const preview = useQuery({
    ...trpc.order.service.terminationPreview.queryOptions({ serviceOrderId: order.id, date }),
    enabled: action === 'terminate'
  })
  const busy = terminate.isPending || reprice.isPending || renew.isPending
  const save = async () => {
    try {
      const common = { serviceOrderId: order.id, expectedUpdatedAt: order.updatedAt }
      if (action === 'terminate') {
        if (!preview.data || preview.isFetching || preview.isError) return
        await terminate.mutateAsync({ ...common, date, reason, expectedUpdatedAt: preview.data.updatedAt })
      } else if (action === 'reprice') {
        if (!ruleId || price === null) {
          void message.error('请选择费用并填写新价格')
          return
        }
        await reprice.mutateAsync({ ...common, ruleId, unitPrice: price, effectiveStart: date, reason })
      } else {
        const result = await renew.mutateAsync({ ...common, term, idempotencyKey })
        void message.success(`已创建待开通续约订单，ID：${result.id}`)
      }
      await onSaved()
      setOpen(false)
    } catch (error) {
      void message.error(error instanceof Error ? error.message : '保存失败')
    }
  }
  const titles = { terminate: '终止服务与尾期核对', reprice: '从未来账期调价', renew: '续约为新服务单' }
  return (
    <Modal
      open={open}
      title={titles[action]}
      width="64rem"
      onCancel={() => setOpen(false)}
      afterClose={onClose}
      closable={!busy}
      keyboard={!busy}
      mask={{ closable: false }}
      cancelButtonProps={{ disabled: busy }}
      onOk={() => void save()}
      confirmLoading={busy}
      okButtonProps={{
        disabled:
          (action !== 'renew' && !reason.trim()) ||
          (action === 'terminate' &&
            (!preview.data ||
              preview.isFetching ||
              preview.isError ||
              preview.data.affected.some((bill) => bill.requiresAdjustment)))
      }}
    >
      {action === 'renew' ? (
        <ServiceTermEditor value={term} onChange={setTerm} disabled={busy} />
      ) : (
        <div className="space-y-4">
          {action === 'reprice' && (
            <>
              <label className="block">
                费用项目
                <Select
                  className="w-full"
                  aria-label="费用项目"
                  value={ruleId}
                  onChange={setRuleId}
                  options={rules
                    .filter((rule) => rule.chargeMode === 'recurring')
                    .map((rule) => ({ value: rule.ruleId, label: `${rule.feeName}（规则 ${rule.ruleId}）` }))}
                />
              </label>
              <label className="block">
                新单价
                <InputNumber
                  className="w-full"
                  aria-label="新单价"
                  min={0}
                  precision={2}
                  value={price}
                  onChange={setPrice}
                />
              </label>
            </>
          )}
          <label className="block">
            {action === 'terminate' ? '最后服务日（含当天）' : '调价生效日（未来账期开始）'}
            <DatePicker
              className="w-full"
              aria-label="生效日期"
              value={dayjs(date)}
              allowClear={false}
              onChange={(value) => {
                if (value) setDate(value.format('YYYY-MM-DD'))
              }}
            />
          </label>
          <Input.TextArea
            aria-label="操作原因"
            placeholder="请填写原因"
            maxLength={1000}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
          {action === 'terminate' && (
            <>
              <Alert
                type="info"
                title="正式账单须先按预览金额调整，退款和押金另行处理。确认终止后系统会修正尾期草稿并作废未来草稿。"
              />
              {preview.isError && <Alert type="error" title={preview.error.message} />}
              {preview.isFetching && <Spin />}
              {preview.data?.affected.map((bill) => (
                <p key={bill.id}>
                  {bill.billNo}：当前 ¥{formatAmount(bill.currentAmount)} → 应收 ¥{formatAmount(bill.expectedAmount)}，
                  {bill.requiresAdjustment ? '请先调整正式账单' : '可确认'}
                </p>
              ))}
            </>
          )}
        </div>
      )}
    </Modal>
  )
}
